import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import PulseLoader from '@/components/PulseLoader';
import { authService } from '../../../utils/authService';
import { formatIstClock } from '../../../utils/formatIstClock';
import {
  endLiveSession,
  isLiveKitProvider,
  syncParticipantInfoBeforeEnd,
} from '../../../liveSession/api';
import { fetchCoursesByTrack } from "../../../utils/courseUtils";
import { fetchBatchesByCourseSingle } from "../../../utils/batchUtils";
import { fetchAllTracksForCourses } from "../../../utils/trackUtils";
import { Session, Track, Course, Batch } from './sessionUtils/sessionTypes';
import { createSessionNameSlug, getRoutePrefix, getCreateSessionPath, convertTo12HourFormat, getSessionAnalyticsPath, filterOngoingSessions } from './sessionUtils/sessionUtils';
import SessionFilters from './sessionUtils/SessionFilters';
import CompletedSessions from './sessionUtils/CompletedSessions';
import UpcomingSessions from './sessionUtils/UpcomingSessions';
import OngoingSessions from './sessionUtils/OngoingSessions';
import ManageSessionStudentsModal from './sessionUtils/ManageSessionStudentsModal';
import { needsLiveKitPodStatusPoll } from './sessionUtils/LiveKitPodStatusDot';
import * as XLSX from 'xlsx';
import {
  DEFAULT_SESSION_BUCKET,
  SESSION_BUCKET,
  SESSION_BUCKET_STORAGE_KEY,
  isSessionBucketKey,
  type SessionBucketKey,
} from "../../../constants/constants";

const DEFAULT_SESSION_PAGE_SIZE = 10;

/** Clock label for live room top bar (e.g. "1:23 PM"). */
const formatLiveEndClock = (timeRaw?: string): string | undefined => {
  const t = String(timeRaw || "").trim();
  if (!t) return undefined;
  if (/AM|PM/i.test(t)) return t.replace(/^0/, "");
  try {
    return convertTo12HourFormat(t).replace(/^0/, "");
  } catch {
    return t;
  }
};

const liveKitRoomNavState = (session: Session, extras?: {
  courseLabel?: string;
  batchLabel?: string;
  startTimeOverride?: string;
  expectedEndOverride?: string;
  durationMinutesOverride?: number;
  workshopDayIndex?: number;
}) => {
  const endClock = formatLiveEndClock(
    extras?.expectedEndOverride ||
      session.Expected_End_Time_time ||
      session.End_Time_time
  );
  const startClock =
    formatLiveEndClock(extras?.startTimeOverride) ||
    formatLiveEndClock(session.Actual_Start_Time_time) ||
    formatLiveEndClock(session.Start_Time);
  const durationMinutes =
    extras?.durationMinutesOverride ||
    Number(session.session_duration) ||
    60;

  const dayIndex =
    typeof extras?.workshopDayIndex === "number"
      ? extras.workshopDayIndex
      : null;
  const fullPlan = Array.isArray(session.topic_plan) ? session.topic_plan : [];
  const dayPlan =
    dayIndex == null
      ? fullPlan
      : fullPlan.filter((t: Record<string, unknown>) => {
          const raw = t?.workshop_day_index ?? t?.workshopDayIndex;
          if (raw != null && !Number.isNaN(Number(raw))) {
            return Number(raw) === dayIndex;
          }
          const text = `${t?.description || ""} ${t?.title || ""}`;
          const match = /\bday\s*(\d+)\b/i.exec(String(text));
          if (match) return Math.max(0, Number(match[1]) - 1) === dayIndex;
          return dayIndex === 0;
        });

  const topicNames = dayPlan
    .map((t: Record<string, unknown>) =>
      String(t?.title ?? t?.name ?? "").trim()
    )
    .filter(Boolean);
  return {
    sessionName: session.Session_Topic,
    scheduledEndTime: endClock,
    expectedEndTime: endClock,
    startTime: startClock || "10:00 AM",
    durationMinutes,
    topicPlan: dayPlan.length ? dayPlan : fullPlan,
    workshopDayIndex: dayIndex ?? undefined,
    topicCovered:
      topicNames.join(", ") || session.Session_Topic || "",
    courseLabel:
      session.course_name ||
      extras?.courseLabel ||
      "",
    batchLabel:
      session.batch_name ||
      extras?.batchLabel ||
      "",
    category: session.live_session_category || "",
    trainerName: (() => {
      const name = String(session.conductedby || "").trim();
      if (!name || /^user\d+$/i.test(name) || name === session.session_assigned_to) {
        return "";
      }
      return name;
    })(),
    streamingMode: "one-way",
    courseId: session.course_id,
    batchId: session.batch_id,
  };
};

const readStoredSessionBucket = (): SessionBucketKey => {
  const stored = sessionStorage.getItem(SESSION_BUCKET_STORAGE_KEY);
  if (isSessionBucketKey(stored)) {
    return stored;
  }
  return DEFAULT_SESSION_BUCKET;
};

const ViewSessions: React.FC = () => {
  const location = useLocation();
  const { bucket: routeBucket } = useParams<{ bucket?: string }>();

  const resolveInitialBucket = (): SessionBucketKey => {
    const raw = (routeBucket || new URLSearchParams(location.search).get("bucket") || "")
      .toLowerCase()
      .replace("completed", "past");
    if (isSessionBucketKey(raw)) {
      return raw;
    }
    return readStoredSessionBucket();
  };

  const initialSessionBucket = resolveInitialBucket();
  const [pastSessions, setPastSessions] = useState<Session[]>([]);
  const [upcomingSessions, setUpcomingSessions] = useState<Session[]>([]);
  const [ongoingSessions, setOngoingSessions] = useState<Session[]>([]);
  const [sessionCounts, setSessionCounts] = useState<Record<SessionBucketKey, number>>({
    [SESSION_BUCKET.ONGOING]: 0,
    [SESSION_BUCKET.UPCOMING]: 0,
    [SESSION_BUCKET.PAST]: 0,
  });
  const [bucketSessionsLoading, setBucketSessionsLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedSessionType, setSelectedSessionType] = useState<string>(initialSessionBucket);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<string>(() => sessionStorage.getItem('meet_track_id') || '');
  const [selectedCourse, setSelectedCourse] = useState<string>(() => sessionStorage.getItem('meet_course_id') || '');
  const [selectedBatch, setSelectedBatch] = useState<string>(() => sessionStorage.getItem('meet_batch_id') || '');
  const [selectedCategory, setSelectedCategory] = useState<string>(
    () => sessionStorage.getItem('meet_session_category') || '',
  );
  const [loadingTracks, setLoadingTracks] = useState<boolean>(false);
  const [loadingCourses, setLoadingCourses] = useState<boolean>(false);
  const [loadingBatches, setLoadingBatches] = useState<boolean>(false);
  const [filtersReady, setFiltersReady] = useState<boolean>(false);
  const filtersHydratedRef = useRef<boolean>(false);
  const navigate = useNavigate();
  const [loadingSessions, setLoadingSessions] = useState<Set<number>>(new Set());
  const [loadingStarts, setLoadingStarts] = useState<Set<number>>(new Set());
  const [actionError, setActionError] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [selectedSessions, setSelectedSessions] = useState<Session[]>([]);
  const [manageStudentsSession, setManageStudentsSession] = useState<Session | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_SESSION_PAGE_SIZE);

  const selectedSessionTypeRef = useRef<SessionBucketKey>(initialSessionBucket);
  const currentPageRef = useRef<number>(1);
  const pageSizeRef = useRef<number>(DEFAULT_SESSION_PAGE_SIZE);
  useEffect(() => {
    selectedSessionTypeRef.current = selectedSessionType as SessionBucketKey;
  }, [selectedSessionType]);
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  const filtersPayload = useCallback(() => {
    return {
      track_id: selectedTrack,
      course_id: selectedCourse,
      batch_id: selectedBatch,
      ...(selectedCategory
        ? { live_session_category: selectedCategory }
        : {}),
    };
  }, [selectedTrack, selectedCourse, selectedBatch, selectedCategory]);

  const fetchSessionCountsOnly = useCallback(async (): Promise<void> => {
    if (!selectedTrack || !selectedCourse || !selectedBatch) return;
    try {
      // Step 1: request only bucket counts for badges.
      const response = await authService.post("/session/attendances/", {
        ...filtersPayload(),
        counts_only: true,
      });
      // Step 2: normalize and store counts for all tabs.
      const payload = response.data as { ongoing?: number; upcoming?: number; past?: number };
      setSessionCounts({
        [SESSION_BUCKET.ONGOING]: Number(payload.ongoing ?? 0),
        [SESSION_BUCKET.UPCOMING]: Number(payload.upcoming ?? 0),
        [SESSION_BUCKET.PAST]: Number(payload.past ?? 0),
      });
    } catch (error) {
      console.error("Error fetching session counts:", error);
    }
  }, [filtersPayload, selectedTrack, selectedCourse, selectedBatch]);

  const fetchSessionsForBucket = useCallback(
    async (
      bucket: SessionBucketKey,
      pageToLoad: number = currentPageRef.current,
      options?: { silent?: boolean; skipCache?: boolean }
    ): Promise<void> => {
      if (!selectedTrack || !selectedCourse || !selectedBatch) return;
      const silent = Boolean(options?.silent);
      if (!silent) setBucketSessionsLoading(true);
      try {
        // Step 1: fetch only selected bucket + selected page from backend.
        const response = await authService.post("/session/attendances/", {
          ...filtersPayload(),
          session_bucket: bucket,
          counts_only: false,
          page: pageToLoad,
          page_size: pageSizeRef.current,
          ...(options?.skipCache ? { skip_cache: true } : {}),
        });
        // Step 2: support both legacy array response and new paginated payload.
        const payload = response.data as any;
        const rows = (Array.isArray(payload) ? payload : (payload?.sessions || [])) as Session[];
        // Step 3: refresh badge counts from backend payload when available.
        if (!Array.isArray(payload) && payload?.counts) {
          setSessionCounts({
            [SESSION_BUCKET.ONGOING]: Number(payload.counts.ongoing ?? 0),
            [SESSION_BUCKET.UPCOMING]: Number(payload.counts.upcoming ?? 0),
            [SESSION_BUCKET.PAST]: Number(payload.counts.past ?? 0),
          });
        }
        // Step 4: update only the currently requested bucket list.
        if (bucket === SESSION_BUCKET.ONGOING) {
          setOngoingSessions(filterOngoingSessions(rows));
        } else if (bucket === SESSION_BUCKET.UPCOMING) {
          setUpcomingSessions(rows);
        } else {
          setPastSessions(rows);
        }
      } catch (error) {
        console.error(`Error fetching ${bucket} sessions:`, error);
      } finally {
        if (!silent) setBucketSessionsLoading(false);
      }
    },
    [filtersPayload, selectedTrack, selectedCourse, selectedBatch]
  );

  const refreshCountsAndActiveBucket = useCallback(async (): Promise<void> => {
    await Promise.all([
      fetchSessionCountsOnly(),
      fetchSessionsForBucket(selectedSessionTypeRef.current, currentPageRef.current),
    ]);
  }, [fetchSessionCountsOnly, fetchSessionsForBucket]);

  // Poll LiveKit pod fields so ACTIONS status dots flip red → yellow → green
  // without a full page reload while pods provision in the background.
  useEffect(() => {
    const bucket = selectedSessionType as SessionBucketKey;
    if (
      bucket !== SESSION_BUCKET.ONGOING &&
      bucket !== SESSION_BUCKET.UPCOMING
    ) {
      return;
    }
    const rows =
      bucket === SESSION_BUCKET.ONGOING ? ongoingSessions : upcomingSessions;
    const needsPoll = rows.some(needsLiveKitPodStatusPoll);
    if (!needsPoll) return;

    const id = window.setInterval(() => {
      void fetchSessionsForBucket(bucket, currentPageRef.current, {
        silent: true,
        skipCache: true,
      });
    }, 5000);
    return () => window.clearInterval(id);
  }, [
    selectedSessionType,
    ongoingSessions,
    upcomingSessions,
    fetchSessionsForBucket,
  ]);

  // Keep bucket in sync when URL/search provides a bucket (e.g. legacy redirects).
  useEffect(() => {
    const raw = (routeBucket || new URLSearchParams(location.search).get("bucket") || "")
      .toLowerCase()
      .replace("completed", "past");
    if (!isSessionBucketKey(raw)) {
      return;
    }
    if (raw !== selectedSessionTypeRef.current) {
      sessionStorage.setItem(SESSION_BUCKET_STORAGE_KEY, raw);
      setSelectedSessionType(raw);
      setSelectedSessions([]);
      setCurrentPage(1);
    }
    if (selectedTrack && selectedCourse && selectedBatch) {
      void Promise.all([
        fetchSessionCountsOnly(),
        fetchSessionsForBucket(raw, 1),
      ]);
    }
  }, [
    routeBucket,
    location.search,
    selectedTrack,
    selectedCourse,
    selectedBatch,
    fetchSessionCountsOnly,
    fetchSessionsForBucket,
  ]);

  const fetchTracks = async (forceRefresh = true): Promise<Track[]> => {
    setLoadingTracks(true);
    try {
      // Always hit API on View Sessions mount so a stale/corrupt sessionStorage
      // cache cannot leave the Track dropdown empty.
      const data = await fetchAllTracksForCourses(forceRefresh);
      setTracks(Array.isArray(data) ? data : []);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching tracks:", error);
      setTracks([]);
      return [];
    } finally {
      setLoadingTracks(false);
      setIsLoading(false);
    }
  };

  const fetchCourses = async (trackId: string): Promise<Course[]> => {
    setLoadingCourses(true);
    try {
      const data = await fetchCoursesByTrack(trackId);
      setCourses(data);
      return data;
    } catch (error) {
      console.error("Error fetching courses:", error);
      setCourses([]);
      return [];
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchBatches = async (courseId: string): Promise<Batch[]> => {
    setLoadingBatches(true);
    try {
      const data = await fetchBatchesByCourseSingle(courseId);
      setBatches(data);
      return data;
    } catch (error) {
      console.error("Error fetching batches:", error);
      setBatches([]);
      return [];
    } finally {
      setLoadingBatches(false);
    }
  };

  const persistFilters = useCallback((trackId: string, courseId: string, batchId: string, batchName?: string, courseName?: string) => {
    if (trackId) sessionStorage.setItem('meet_track_id', trackId);
    else sessionStorage.removeItem('meet_track_id');
    if (courseId) sessionStorage.setItem('meet_course_id', courseId);
    else sessionStorage.removeItem('meet_course_id');
    if (batchId) sessionStorage.setItem('meet_batch_id', batchId);
    else sessionStorage.removeItem('meet_batch_id');
    if (batchName) sessionStorage.setItem('meet_batch_name', batchName);
    else if (!batchId) sessionStorage.removeItem('meet_batch_name');
    if (courseName) sessionStorage.setItem('meet_course_name', courseName);
    else if (!courseId) sessionStorage.removeItem('meet_course_name');
  }, []);

  const handleTrackChange = useCallback(async (trackId: string) => {
    setSelectedTrack(trackId);
    setSelectedCourse('');
    setSelectedBatch('');
    setCourses([]);
    setBatches([]);
    persistFilters(trackId, '', '');
    if (trackId) {
      await fetchCourses(trackId);
    }
  }, [persistFilters]);

  const handleCourseChange = useCallback(async (courseId: string) => {
    setSelectedCourse(courseId);
    setSelectedBatch('');
    setBatches([]);
    const courseName =
      courses.find((c) => c.course_id === courseId)?.course_name || '';
    persistFilters(selectedTrack, courseId, '', undefined, courseName);
    if (courseId) {
      await fetchBatches(courseId);
    }
  }, [persistFilters, selectedTrack, courses]);

  const handleBatchChange = useCallback((batchId: string) => {
    setSelectedBatch(batchId);
    const batchName = batches.find((b) => b.batch_id === batchId)?.batch_name || '';
    const courseName =
      courses.find((c) => c.course_id === selectedCourse)?.course_name ||
      sessionStorage.getItem('meet_course_name') ||
      '';
    persistFilters(selectedTrack, selectedCourse, batchId, batchName, courseName);
  }, [persistFilters, selectedTrack, selectedCourse, batches, courses]);

  const handleCategoryChange = useCallback((category: string) => {
    // ---------------------------------------------------------------
    // step 1: Persist session-type filter and reload current bucket
    // ---------------------------------------------------------------
    setSelectedCategory(category);
    if (category) sessionStorage.setItem('meet_session_category', category);
    else sessionStorage.removeItem('meet_session_category');
    setCurrentPage(1);
    setSelectedSessions([]);
  }, []);

  // One-time hydrate: restore stored Track → Course → Batch without clearing them.
  useEffect(() => {
    if (filtersHydratedRef.current) return;
    filtersHydratedRef.current = true;

    const currentPath = window.location.pathname;
    const isAdminRoute = currentPath.startsWith('/admin');
    const hasAdminPermission = sessionStorage.getItem("Admin");
    if (isAdminRoute && !hasAdminPermission) {
      // Still load tracks before redirect so remount isn't required for data.
      void fetchTracks(true);
      navigate(currentPath.replace('/admin', '/trainer'));
      return;
    }

    const storedTrack = sessionStorage.getItem('meet_track_id') || '';
    const storedCourse = sessionStorage.getItem('meet_course_id') || '';
    const storedBatch = sessionStorage.getItem('meet_batch_id') || '';

    void (async () => {
      const trackList = await fetchTracks(true);
      if (!storedTrack) {
        setFiltersReady(true);
        return;
      }

      const trackExists = trackList.some((t) => t.track_id === storedTrack);
      if (!trackExists) {
        persistFilters('', '', '');
        setSelectedTrack('');
        setSelectedCourse('');
        setSelectedBatch('');
        setFiltersReady(true);
        return;
      }

      setSelectedTrack(storedTrack);
      const courseList = await fetchCourses(storedTrack);
      if (!storedCourse) {
        setFiltersReady(true);
        return;
      }

      const courseExists = courseList.some((c) => c.course_id === storedCourse);
      if (!courseExists) {
        persistFilters(storedTrack, '', '');
        setSelectedCourse('');
        setSelectedBatch('');
        setFiltersReady(true);
        return;
      }

      setSelectedCourse(storedCourse);
      const batchList = await fetchBatches(storedCourse);
      if (!storedBatch) {
        setFiltersReady(true);
        return;
      }

      const batchExists = batchList.some((b) => b.batch_id === storedBatch);
      if (!batchExists) {
        persistFilters(storedTrack, storedCourse, '');
        setSelectedBatch('');
        setFiltersReady(true);
        return;
      }

      setSelectedBatch(storedBatch);
      persistFilters(storedTrack, storedCourse, storedBatch);
      setFiltersReady(true);
    })();
  }, [navigate, persistFilters]);

  // Fetch sessions once filters are ready and batch is selected.
  useEffect(() => {
    if (!filtersReady) return;

    if (selectedBatch && selectedTrack && selectedCourse) {
      persistFilters(selectedTrack, selectedCourse, selectedBatch);
      setCurrentPage(1);
      void Promise.all([
        fetchSessionCountsOnly(),
        fetchSessionsForBucket(selectedSessionTypeRef.current, 1),
      ]);
      return;
    }

    setPastSessions([]);
    setUpcomingSessions([]);
    setOngoingSessions([]);
    setSessionCounts({
      [SESSION_BUCKET.ONGOING]: 0,
      [SESSION_BUCKET.UPCOMING]: 0,
      [SESSION_BUCKET.PAST]: 0,
    });
    setCurrentPage(1);
  }, [
    filtersReady,
    selectedBatch,
    selectedTrack,
    selectedCourse,
    selectedCategory,
    persistFilters,
    fetchSessionCountsOnly,
    fetchSessionsForBucket,
  ]);

  const sessionActionErrorMessage = (err: unknown, fallback: string): string => {
    const detail = (err as { response?: { data?: { detail?: unknown } } })
      ?.response?.data?.detail;
    if (detail && typeof detail === "object" && detail !== null) {
      const msg = (detail as { message?: unknown }).message;
      if (typeof msg === "string" && msg.trim()) return msg.trim();
    }
    if (typeof detail === "string" && detail.trim()) return detail.trim();
    const message = (err as { message?: unknown })?.message;
    if (typeof message === "string" && message.trim()) return message.trim();
    return fallback;
  };

  const enterLiveKitRoom = (
    session: Session,
    extras?: {
      startTimeOverride?: string;
      expectedEndOverride?: string;
      durationMinutesOverride?: number;
      workshopDayIndex?: number;
    },
  ): void => {
    const prefix = getRoutePrefix();
    navigate(`${prefix}/live-session/${session.id}`, {
      state: liveKitRoomNavState(session, {
        courseLabel:
          session.course_name ||
          courses.find((c) => c.course_id === selectedCourse)?.course_name ||
          sessionStorage.getItem("meet_course_name") ||
          "",
        batchLabel:
          session.batch_name ||
          (Array.isArray(session.batch_id) && session.batch_id.length > 1
            ? ""
            : batches.find((b) => b.batch_id === selectedBatch)?.batch_name ||
              sessionStorage.getItem("meet_batch_name") ||
              "") ||
          "",
        startTimeOverride: extras?.startTimeOverride,
        expectedEndOverride: extras?.expectedEndOverride,
        durationMinutesOverride: extras?.durationMinutesOverride,
        workshopDayIndex: extras?.workshopDayIndex,
      }),
    });
  };

  const handleStartSession = async (
    session: Session,
    dayIndex?: number,
  ): Promise<void> => {
    setLoadingStarts(prev => new Set(prev).add(session.id));
    setActionError(null);
    const isLiveKit = isLiveKitProvider(session.session_provider);

    try {
      const payload: { session_id: string; day_index?: number } = {
        session_id: session.id.toString(),
      };
      if (typeof dayIndex === 'number') {
        payload.day_index = dayIndex;
      }
      const startRes = await authService.post(
        "/sessions/start/",
        payload
      );
      const startData = startRes?.data || {};
      const actualIso = String(startData.session_actual_starttime || "").trim();
      const expectedIso = String(startData.session_expected_endtime || "").trim();
      let startOverride = "";
      let expectedOverride = "";
      let durationOverride: number | undefined;
      if (actualIso) {
        startOverride = formatIstClock(actualIso);
      }
      if (actualIso && expectedIso) {
        const a = new Date(actualIso);
        const e = new Date(expectedIso);
        if (!Number.isNaN(a.getTime()) && !Number.isNaN(e.getTime())) {
          durationOverride = Math.max(
            15,
            Math.round((e.getTime() - a.getTime()) / 60000),
          );
          expectedOverride = formatIstClock(expectedIso);
        }
      }

      // LiveKit: Start = mark live + enter room (no second Join click).
      if (isLiveKit) {
        enterLiveKitRoom(session, {
          startTimeOverride: startOverride,
          expectedEndOverride: expectedOverride,
          durationMinutesOverride: durationOverride,
          workshopDayIndex: dayIndex,
        });
      } else {
        window.open(session.meetlink, '_blank', 'noopener,noreferrer');
      }

      await refreshCountsAndActiveBucket();
    } catch (error) {
      console.error("Error starting session:", error);
      // If session is already live, still enter the room for LiveKit.
      const msg = sessionActionErrorMessage(
        error,
        "Unable to start this session. Please try again.",
      ).toLowerCase();
      const alreadyLive =
        msg.includes("already live") || msg.includes("already started");
      if (isLiveKit && alreadyLive) {
        enterLiveKitRoom(session, { workshopDayIndex: dayIndex });
        await refreshCountsAndActiveBucket();
        return;
      }
      setActionError({
        title: "Error Starting Session",
        message: sessionActionErrorMessage(
          error,
          "Unable to start this session. Please try again.",
        ),
      });
    } finally {
      setLoadingStarts(prev => {
        const newSet = new Set(prev);
        newSet.delete(session.id);
        return newSet;
      });
    }
  };

  const handleJoinSession = (session: Session, dayIndex?: number): void => {
    if (isLiveKitProvider(session.session_provider)) {
      enterLiveKitRoom(session, { workshopDayIndex: dayIndex });
      return;
    }
    if (session.meetlink) {
      window.open(session.meetlink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleEndSession = async (session: Session): Promise<void> => {
    const isLiveKit = isLiveKitProvider(session.session_provider);
    const user = {
      email: sessionStorage.getItem('Email') || '',
      name: sessionStorage.getItem('Name') || ''
    };

    setLoadingSessions(prev => new Set(prev).add(session.id));
    setActionError(null);

    try {
      // Meet attendance sync only applies to Google Meet sessions.
      if (!isLiveKit && session.meetlink) {
        await syncParticipantInfoBeforeEnd({
          meeting_link: session.meetlink,
          email: user.email,
          name: session.conductedby,
          session_id: session.id,
        });
      }

      await endLiveSession(
        session.id,
        sessionStorage.getItem('Id') || ''
      );

      // Optimistically drop fully finished sessions from Ongoing before refresh.
      setOngoingSessions((prev) =>
        filterOngoingSessions(
          prev.map((s) =>
            s.id === session.id
              ? {
                  ...s,
                  session_status: "completed",
                  day_schedules: (s.day_schedules || []).map((d) =>
                    d.can_join || d.status === "live"
                      ? { ...d, status: "ended", can_join: false, has_ended: true }
                      : d,
                  ),
                }
              : s,
          ),
        ),
      );

      await refreshCountsAndActiveBucket();
    } catch (error) {
      setActionError({
        title: "Error Ending Session",
        message: sessionActionErrorMessage(
          error,
          "This session was not created by you. Only the creator can end the session.",
        ),
      });
    } finally {
      setLoadingSessions(prev => {
        const newSet = new Set(prev);
        newSet.delete(session.id);
        return newSet;
      });
    }
  };

  const handleShowReport = (session: Session): void => {
    const prefix = getRoutePrefix();
    const sessionNameSlug = createSessionNameSlug(session.Session_Topic);
    const batchLabel =
      session.batch_name ||
      batches.find((b) => b.batch_id === selectedBatch)?.batch_name ||
      selectedBatch ||
      '';

    let plannedDurationMinutes: number | undefined;
    try {
      if (session.Date && session.Start_Time && session.Expected_End_Time_date && session.Expected_End_Time_time) {
        const start = new Date(`${session.Date}T${session.Start_Time}`);
        const end = new Date(
          `${session.Expected_End_Time_date}T${session.Expected_End_Time_time}`,
        );
        const mins = Math.round((end.getTime() - start.getTime()) / 60000);
        if (Number.isFinite(mins) && mins > 0) plannedDurationMinutes = mins;
      }
    } catch {
      plannedDurationMinutes = undefined;
    }

    navigate(getSessionAnalyticsPath(sessionNameSlug, session.id, prefix), {
      state: {
        sessionTitle: session.Session_Topic,
        trainerName: session.conductedby,
        batchLabel,
        durationMinutes: session.session_duration,
        plannedDurationMinutes,
      },
    });
  };

  const handleDeleteSession = async (session: Session): Promise<void> => {
    try {
      const payload = {
        session_id: session.id.toString(),
        session_title: "",
        session_subject: "",
        session_meetlink: "",
        start_date: "",
        start_time: "",
        end_date: "",
        end_time: "",
        session_assigned_to: "",
        session_status: "cancelled",
        track_id: [],
        course_id: [],
        batch_id: [],
        subject_id: []
      };

      await authService.put("/session/update/", payload);
      await refreshCountsAndActiveBucket();
    } catch (error) {
      console.error("Error deleting session:", error);
    }
  };

  const handleSessionsChange = useCallback((sessions: Session[]): void => {
    setSelectedSessions(sessions);
  }, []);

  const sessionTabConfig = [
    {
      key: SESSION_BUCKET.ONGOING,
      label: "Ongoing",
      count: sessionCounts[SESSION_BUCKET.ONGOING],
    },
    {
      key: SESSION_BUCKET.UPCOMING,
      label: "Upcoming",
      count: sessionCounts[SESSION_BUCKET.UPCOMING],
    },
    {
      key: SESSION_BUCKET.PAST,
      label: "Completed",
      count: sessionCounts[SESSION_BUCKET.PAST],
    },
  ];
  const selectedBucketKey = selectedSessionType as SessionBucketKey;
  const selectedBucketCount = Number(sessionCounts[selectedBucketKey] ?? 0);

  const handleTablePaginationChange = useCallback(
    (next: { pageIndex: number; pageSize: number }) => {
      const nextPage = (next.pageIndex ?? 0) + 1;
      const nextPageSize = next.pageSize ?? pageSize;
      setSelectedSessions([]);
      // Step 1: when page size changes, reset to first page and refetch.
      if (nextPageSize !== pageSize) {
        pageSizeRef.current = nextPageSize;
        currentPageRef.current = 1;
        setPageSize(nextPageSize);
        setCurrentPage(1);
        void fetchSessionsForBucket(selectedBucketKey, 1);
        return;
      }
      // Step 2: page changed with same size; fetch that specific page.
      setCurrentPage(nextPage);
      void fetchSessionsForBucket(selectedBucketKey, nextPage);
    },
    [fetchSessionsForBucket, pageSize, selectedBucketKey]
  );

  const handleExport = (): void => {
    let sessionsToExport: Session[] = [];
    
    if (selectedSessionType === SESSION_BUCKET.PAST) {
      sessionsToExport = selectedSessions.length > 0 ? selectedSessions : pastSessions;
    } else if (selectedSessionType === SESSION_BUCKET.ONGOING) {
      sessionsToExport = selectedSessions.length > 0 ? selectedSessions : ongoingSessions;
    } else if (selectedSessionType === SESSION_BUCKET.UPCOMING) {
      sessionsToExport = selectedSessions.length > 0 ? selectedSessions : upcomingSessions;
    }

    // Map sessions to Excel format based on current tab, mirroring visible columns
    const exportData = sessionsToExport.map((session) => {
      const type = (() => {
        const category = String(session.live_session_category || "").toLowerCase();
        if (category === "workshop_onsite") return "Onsite";
        const title = String(session.Session_Topic || "").toUpperCase();
        if (title.startsWith("LW-ONSITE") || /^LW\s/.test(title)) return "Onsite";
        return "Online";
      })();
      const startLabel = `${session.Date || "-"} ${session.Start_Time ? convertTo12HourFormat(session.Start_Time) : "-"}`.trim();
      const endLabel = `${session.Expected_End_Time_date || session.End_Time_date || "-"} ${
        (session.Expected_End_Time_time || session.End_Time_time)
          ? convertTo12HourFormat(session.Expected_End_Time_time || session.End_Time_time)
          : "-"
      }`.trim();

      if (selectedSessionType === SESSION_BUCKET.PAST) {
        const duration = Math.round(Number(session.session_duration)) || "-";
        let attendance = "—";
        const invited = Number(session.attendances?.invited_students_count);
        const attendedCount = Number(session.attendances?.attended_students_count);
        if (
          Number.isFinite(invited) &&
          invited > 0 &&
          Number.isFinite(attendedCount) &&
          attendedCount >= 0
        ) {
          attendance = `${Math.round((attendedCount * 100) / invited)}%`;
        } else if (session.attendances_time) {
          const values = session.attendances_time.split(" / ");
          if (values && values.length === 2) {
            const attendedTime = (Number(values[0]) * 100) / Number(values[1]);
            attendance = `${Math.round(attendedTime)}%`;
          }
        }
        const actualStart = `${session.Actual_Start_Time_date || session.Date || "-"} ${
          session.Actual_Start_Time_time
            ? convertTo12HourFormat(session.Actual_Start_Time_time)
            : session.Start_Time
              ? convertTo12HourFormat(session.Start_Time)
              : "-"
        }`.trim();

        return {
          "Session Name": session.Session_Topic,
          Trainer: session.conductedby,
          Type: type,
          Start: actualStart,
          End: endLabel,
          Duration: typeof duration === "number" ? `${duration} min` : duration,
          Attendance: attendance,
        };
      }

      if (selectedSessionType === SESSION_BUCKET.ONGOING) {
        return {
          "Session Name": session.Session_Topic,
          Trainer: session.conductedby,
          Type: type,
          Start: startLabel,
        };
      }

      return {
        "Session Name": session.Session_Topic,
        Trainer: session.conductedby,
        Type: type,
        Start: startLabel,
        End: endLabel,
      };
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sessions");

    // Generate filename based on session type
    const sessionTypeName =
      selectedSessionType === SESSION_BUCKET.PAST
        ? "Completed"
        : selectedSessionType === SESSION_BUCKET.ONGOING
          ? "Ongoing"
          : "Upcoming";
    
    // Write file
    XLSX.writeFile(wb, `${sessionTypeName}_Sessions.xlsx`);
  };

  const handleCreateSession = (): void => {
    navigate(getCreateSessionPath());
  };

  if (isLoading) {
    return (
      <div
        className="border pt-3 px-2 rounded-2 bg-white my-2 me-2"
        style={{
          height: "calc(100vh - 90px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <PulseLoader color="#2563eb" />
      </div>
    );
  }

  return (
    <div
      className="border pt-2 px-2 rounded-2 bg-white my-2 me-2"
      style={{
        height: "calc(100vh - 90px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          m: 0,
          p: 0,
          gap: 1,
        }}
      >
        <SessionFilters
          selectedTrack={selectedTrack}
          onTrackChange={(id) => void handleTrackChange(id)}
          loadingTracks={loadingTracks}
          tracks={tracks}
          selectedCourse={selectedCourse}
          onCourseChange={(id) => void handleCourseChange(id)}
          loadingCourses={loadingCourses}
          courses={courses}
          selectedBatch={selectedBatch}
          onBatchChange={handleBatchChange}
          loadingBatches={loadingBatches}
          batches={batches}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          onExport={handleExport}
        />

        <Box
          sx={{
            borderRadius: "8px",
            backgroundColor: "#f8fafc",
            border: "1px solid #e5e7eb",
            minHeight: 48,
            display: "flex",
            alignItems: "center",
            px: 1,
            py: 0.75,
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            {sessionTabConfig.map((tab) => {
              const active = selectedSessionType === tab.key;
              return (
                <Button
                  key={tab.key}
                  onClick={() => {
                    sessionStorage.setItem(SESSION_BUCKET_STORAGE_KEY, tab.key);
                    setSelectedSessionType(tab.key);
                    setSelectedSessions([]);
                    setCurrentPage(1);
                    void Promise.all([
                      fetchSessionCountsOnly(),
                      fetchSessionsForBucket(tab.key, 1),
                    ]);
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    px: 1.5,
                    py: 0.5,
                    minWidth: 0,
                    fontWeight: 700,
                    fontSize: 14,
                    color: active ? "#ffffff" : "#475569",
                    backgroundColor: active ? "#2563eb" : "transparent",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: active ? "#1d4ed8" : "#e2e8f0",
                      boxShadow: "none",
                    },
                  }}
                >
                  {tab.label} ({tab.count})
                </Button>
              );
            })}
          </Box>
          <Box sx={{ ml: "auto" }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleCreateSession}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 700,
                whiteSpace: "nowrap",
                bgcolor: "#2563eb",
                px: 1.75,
                py: 0.75,
                boxShadow: "none",
                "&:hover": { bgcolor: "#1d4ed8", boxShadow: "none" },
              }}
            >
              + Create Session
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {bucketSessionsLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.72)",
                zIndex: 2,
              }}
            >
              <PulseLoader color="#2563eb" />
            </Box>
          )}
        {selectedSessionType === SESSION_BUCKET.ONGOING && (
          <OngoingSessions
            sessions={ongoingSessions}
            loadingSessions={loadingSessions}
            loadingStarts={loadingStarts}
            batchLabel={batches.find((b) => b.batch_id === selectedBatch)?.batch_name || ""}
            onEndSession={handleEndSession}
            onDeleteSession={handleDeleteSession}
            onStartSession={handleStartSession}
            onJoinSession={handleJoinSession}
            onShowReport={handleShowReport}
            onManageStudents={setManageStudentsSession}
            onSessionsChange={handleSessionsChange}
            onSessionUpdated={() => {
              void refreshCountsAndActiveBucket();
            }}
            pagination={{ pageIndex: Math.max(currentPage - 1, 0), pageSize }}
            rowCount={selectedBucketCount}
            onPaginationChange={handleTablePaginationChange}
          />
        )}

        {selectedSessionType === SESSION_BUCKET.UPCOMING && (
          <UpcomingSessions
            sessions={upcomingSessions}
            batchLabel={batches.find((b) => b.batch_id === selectedBatch)?.batch_name || ""}
            onDeleteSession={handleDeleteSession}
            onStartSession={handleStartSession}
            onShowReport={handleShowReport}
            onManageStudents={setManageStudentsSession}
            loadingStarts={loadingStarts}
            onSessionsChange={handleSessionsChange}
            pagination={{ pageIndex: Math.max(currentPage - 1, 0), pageSize }}
            rowCount={selectedBucketCount}
            onPaginationChange={handleTablePaginationChange}
          />
        )}

        {selectedSessionType === SESSION_BUCKET.PAST && (
          <CompletedSessions
            sessions={pastSessions}
            batchLabel={batches.find((b) => b.batch_id === selectedBatch)?.batch_name || ""}
            onShowReport={handleShowReport}
            onSessionsChange={handleSessionsChange}
            pagination={{ pageIndex: Math.max(currentPage - 1, 0), pageSize }}
            rowCount={selectedBucketCount}
            onPaginationChange={handleTablePaginationChange}
          />
        )}
        </Box>
      </Box>

      {/* Session action errors (start/end). */}
      <Dialog open={Boolean(actionError)} onClose={() => setActionError(null)}>
        <DialogTitle>{actionError?.title || "Error"}</DialogTitle>
        <DialogContent>
          {actionError?.message || "Something went wrong. Please try again."}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionError(null)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <ManageSessionStudentsModal
        open={Boolean(manageStudentsSession)}
        session={manageStudentsSession}
        onClose={() => setManageStudentsSession(null)}
        onChanged={() => {
          void refreshCountsAndActiveBucket();
        }}
      />
    </div>
  );
};

export default ViewSessions;
